/**
 * roleContextStore — Dual-context auth.
 *
 * Supabase only supports ONE active session per browser, but staff/admin
 * users need to be able to use the storefront *as a customer* without their
 * admin role leaking into customer-only widgets. This tiny store records the
 * active "context" for the signed-in user:
 *
 *   • "customer" — treat as a regular shopper. Storefront fully active,
 *                  admin redirect suppressed, customer row auto-created.
 *   • "admin"    — staff is acting in their staff capacity. Storefront UI
 *                  may still link them to /admin.
 *   • null       — no choice yet (freshly signed in). Storefront should
 *                  prompt them to choose.
 *
 * The choice is keyed by user_id so that signing out / switching account
 * doesn't carry stale context across users.
 */
import { create } from "zustand";

const STORAGE_KEY = "khays_role_context_v1";

export type RoleContext = "customer" | "admin";

interface Persisted {
  // user_id -> chosen context
  [userId: string]: RoleContext;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Persisted) : {};
  } catch {
    return {};
  }
}

function save(state: Persisted) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

interface State {
  /** Map of user_id -> chosen role context. */
  contexts: Persisted;
  /** Returns the chosen context for a given user, or null if none yet. */
  getContext: (userId: string | null | undefined) => RoleContext | null;
  /** Persist a choice for a user. */
  setContext: (userId: string, ctx: RoleContext) => void;
  /** Forget the choice (e.g. on sign-out). */
  clearContext: (userId: string) => void;
}

export const useRoleContextStore = create<State>((set, get) => ({
  contexts: load(),
  getContext: (userId) => {
    if (!userId) return null;
    return get().contexts[userId] ?? null;
  },
  setContext: (userId, ctx) => {
    const next = { ...get().contexts, [userId]: ctx };
    save(next);
    set({ contexts: next });
  },
  clearContext: (userId) => {
    const next = { ...get().contexts };
    delete next[userId];
    save(next);
    set({ contexts: next });
  },
}));

/** Convenience non-hook accessor for code paths outside React. */
export function getRoleContext(userId: string | null | undefined): RoleContext | null {
  return useRoleContextStore.getState().getContext(userId);
}
export function setRoleContext(userId: string, ctx: RoleContext) {
  useRoleContextStore.getState().setContext(userId, ctx);
}
